import { Component, inject, OnInit } from '@angular/core';
import {
  AllowedCountryType,
  BaseWidgetProperties,
  BeneficiaryRequirementsResponse,
  CommonUiZoneWidgetConfig,
  FormFieldConfig,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  WidgetLayoutAttributes,
} from '@finxone-platform/shared/sys-config-types';
import {
  BeneficiaryFormService,
  type BeneficiaryFormType,
  FormGroupControls,
} from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-form.service';
import { FormGroup } from '@angular/forms';
import { setFieldValidation } from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-v2.utils';
import { map, take } from 'rxjs';
import { countryCodeToCircleFlag } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { RailTypeEnum } from '@finxone-platform/form-action';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-beneficiary-address-widget',
  templateUrl: './beneficiary-address-widget.component.html',
  styleUrl: './beneficiary-address-widget.component.css',
})
export class BeneficiaryAddressWidgetComponent implements OnInit {
  public beneficiaryForm: FormGroup<FormGroupControls<BeneficiaryFormType>>;
  public beneficiaryAddressFormGroup: FormGroup;
  public inputFieldConfigs: FormFieldConfig[];
  public countryList: AllowedCountryType[] = [];
  public widgetProperties: BaseWidgetProperties;

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    public dataIn: ProviderClass<WidgetLayoutAttributes>,
    public beneficiaryFormService: BeneficiaryFormService,
    private configService: ConfigService,
  ) {}

  public ngOnInit(): void {
    this.beneficiaryForm = this.beneficiaryFormService.beneficiaryForm;
    this.beneficiaryAddressFormGroup = this.beneficiaryForm.get('beneficiaryAddress') as FormGroup;

    this.loadCountryList();

    this.widgetProperties = this.dataIn?.attri?.widgetProperties;

    if (this.dataIn?.attri['childComponents']) {
      this.inputFieldConfigs = this.dataIn.attri['childComponents'].map(
        (widget: CommonUiZoneWidgetConfig) => {
          const widgetAttri = widget.attributes as UiZoneWidgetAttributeConfig;
          return widgetAttri?.widgetProperties?.inputFieldConfig as FormFieldConfig;
        },
      );
    }

    this.beneficiaryFormService
      .getAndSetBeneficiaryRequirements()
      .subscribe((beneficiaryRequirements: BeneficiaryRequirementsResponse) => {
        const bankAccountReqs = beneficiaryRequirements.beneficiaryBankAccount;
        const addressReqs = beneficiaryRequirements?.beneficiaryAddress;
        const isSepaTarget2 = this.beneficiaryForm.get('paymentRail')?.value === RailTypeEnum.SEPA_TARGET_2;
        if (addressReqs && (bankAccountReqs.requirements.beneficiaryAddress == 'required' || isSepaTarget2)) {
          Object.entries(addressReqs).forEach(([key, value]) => {
            if (value?.requirement === 'required') {
              setFieldValidation(key, this.inputFieldConfigs, 'required', true);
            }
          });

          this.loadCountryList();
        }
      });
  }

  private loadCountryList(): void {
    this.configService
      .getCountries()
      .pipe(
        take(1),
        map(
          (countries) =>
            countries?.map(({ name, code }) => ({
              title: name,
              key: code,
              currencyFlag: countryCodeToCircleFlag(code, '24px'),
            })) ?? [],
        ),
      )
      .subscribe((list) => {
        this.countryList = list;
      });
  }

  public findCountryByKey(key: string): AllowedCountryType {
    return this.countryList.find((country) => country.key === key) as AllowedCountryType;
  }

  public countryHasStates(): boolean {
    return (
      this.beneficiaryForm.get('beneficiaryAddress')?.get('country')?.value === 'US' ||
      this.beneficiaryForm.get('beneficiaryAddress')?.get('country')?.value === 'CA' ||
      this.beneficiaryForm.get('beneficiaryAddress')?.get('country')?.value === 'MX'
    );
  }
}
